// REACT IMPORTS
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./ArchiveManager.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall, putAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { CircularProgress, Button, Collapse } from "@mui/material";

// CUSTOM COMPONENTS
import DropboxFileSelector from "../../Misc/DropboxFileSelector/DropboxFileSelector";

import ConfirmFolderMoveDialog from "./ConfirmFolderMoveDialog/ConfirmFolderMoveDialog";

// CONSTANTS
import { addToAutomationLog } from "../../../constants/logs";

// REACT COMPONENT
const ArchiveManager = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [isArchiveSelected, setIsArchiveSelected] = useState(false);
	const [isRestoreSelected, setIsRestoreSelected] = useState(false);

	const [filePath, setFilePath] = useState(null);
	const [highlightedFilePath, setHighlightedFilePath] = useState(null);

	const [destinationPath, setDestinationPath] = useState("");

	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

	// LOADING STATES
	const [isLoading, setIsLoading] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECTS
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Archive Manager",
			employeeID: employeeID,
			notes: "Accessed the Archive Manager page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		if (highlightedFilePath) determineDestinationPath();
	}, [highlightedFilePath]);

	useEffect(() => {
		console.log("Destination Path: ", destinationPath);
	}, [destinationPath]);

	// INPUT HANDLERS
	const handleArchiveClick = () => {
		if (isArchiveSelected) {
			// If already selected, do nothing
			return;
		}

		setIsArchiveSelected(true);
		setIsRestoreSelected(false);

		setFilePath("/Projects"); // Reset file path when archiving
		setHighlightedFilePath(""); // Reset highlighted file path
	};

	const handleRestoreClick = () => {
		if (isRestoreSelected) {
			// If already selected, do nothing
			return;
		}

		setIsArchiveSelected(false);
		setIsRestoreSelected(true);

		setFilePath("/Archives"); // Reset file path when restoring
		setHighlightedFilePath(null); // Reset highlighted file path
	};

	const backButtonClicked = () => {
		if (
			filePath === null ||
			filePath === "/Projects" ||
			filePath === "/Archives"
		) {
			return;
		}

		let newPath = filePath
			.split("/")
			.slice(0, filePath.split("/").length - 1);
		setFilePath(newPath.join("/"));
		setHighlightedFilePath(null);
	};

	const folderClicked = (folder) => {
		if (filePath.split("/").length === 3) {
			if (highlightedFilePath === `${filePath}/${folder}`) {
				// If a folder is already highlighted, unhighlight it
				setHighlightedFilePath("");
			} else {
				setHighlightedFilePath(`${filePath}/${folder}`);
			}
		} else {
			setHighlightedFilePath("");
		}

		if (filePath.split("/").length >= 3) {
			return;
		}

		setFilePath(`${filePath}/${folder}`);
	};

	const handleConfirmationDialogOpen = () => {
		setConfirmationDialogOpen(true);
	};

	const handleConfirmationDialogClose = () => {
		setConfirmationDialogOpen(false);
	};

	const handleConfirmPressed = async () => {
		if (!highlightedFilePath || !destinationPath) return;

		let res = await moveFolder(highlightedFilePath, destinationPath);
		if (res.error) {
			console.log("Error moving folder: ", res.error);
		} else {
			await addToAutomationLog(
				instance,
				accounts[0],
				employeeID,
				true,
				`'${highlightedFilePath}' moved to '${destinationPath}' successfully.`,
				"Archive Manager"
			);
			console.log("Folder moved successfully: ", res);
		}
	};

	// API FUNCTIONS
	const moveFolder = async (fromPath, toPath) => {
		setIsLoading(true);

		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/dropbox/folders/move",
				{ fromPath: fromPath, toPath: toPath }
			);

			await updatePropertyRecordDropboxPath();
			await updateProjectRecordDropboxPath();
			await updatePhaseRecordDropboxPath();

			setIsLoading(false);

			handleConfirmationDialogClose();
			resetArchiveManager();

			return res;
		} catch (error) {
			console.log("Error in moveFolder: ", error);
			return { error: error };
		}
	};

	const updatePropertyRecordDropboxPath = async () => {
		try {
			const res = await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/property/update/dropboxPath",
				{
					to_path: destinationPath,
					from_path: highlightedFilePath,
				}
			);

			return res;
		} catch (error) {
			console.error("Error determining property record: ", error);
			return null;
		}
	};

	const updateProjectRecordDropboxPath = async () => {
		try {
			const res = await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/project/update/dropboxPath",
				{
					to_path: destinationPath,
					from_path: highlightedFilePath,
				}
			);

			return res;
		} catch (error) {
			console.error("Error determining property record: ", error);
			return null;
		}
	};

	const updatePhaseRecordDropboxPath = async () => {
		try {
			const res = await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/phase/update/dropboxPath",
				{
					to_path: destinationPath,
					from_path: highlightedFilePath,
				}
			);

			return res;
		} catch (error) {
			console.error("Error determining property record: ", error);
			return null;
		}
	};

	// HELPER FUNCTIONS
	const determineDestinationPath = () => {
		let splitPath = highlightedFilePath?.split("/");

		if (isArchiveSelected) {
			// Archiving
			splitPath[1] = "Archives";
		} else if (isRestoreSelected) {
			// Restoring
			splitPath[1] = "Projects";
		}

		setDestinationPath(splitPath.join("/"));
	};

	const resetArchiveManager = () => {
		setIsArchiveSelected(false);
		setIsRestoreSelected(false);
		setFilePath(null);
		setHighlightedFilePath(null);
		setDestinationPath("");
		setConfirmationDialogOpen(false);
	};

	// RENDER
	return (
		<div className="ArchiveManager">
			<div className="archiveManagerHeader">
				<div className="archiveManagerHeaderTitle">Archive Manager</div>
				<div className="archiveManagerHeaderActions">
					<Button
						variant="outlined"
						color="error"
						onClick={resetArchiveManager}
						disabled={isLoading}
					>
						Reset
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleConfirmationDialogOpen}
						disabled={!highlightedFilePath}
					>
						Submit
					</Button>
				</div>
			</div>
			<div className="archiveManagerContent">
				<ArchiveOrUnarchiveCard
					isArchiveSelected={isArchiveSelected}
					isRestoreSelected={isRestoreSelected}
					handleArchiveClick={handleArchiveClick}
					handleRestoreClick={handleRestoreClick}
				/>
				<Collapse
					sx={{ display: "flex", width: "stretch" }}
					in={isArchiveSelected || isRestoreSelected}
				>
					<div className="archiveManagerFileSelector">
						<DropboxFileSelector
							filePath={filePath}
							setFilePath={setFilePath}
							bgColor={isArchiveSelected ? "#B53943" : "#568C5B"}
							backButtonClicked={backButtonClicked}
							folderClicked={folderClicked}
							highlightedFilePath={highlightedFilePath}
						/>
					</div>
				</Collapse>
			</div>
			<ConfirmFolderMoveDialog
				open={confirmationDialogOpen}
				onClose={handleConfirmationDialogClose}
				isArchiveSelected={isArchiveSelected}
				isRestoreSelected={isRestoreSelected}
				fromPath={highlightedFilePath}
				toPath={destinationPath}
				handleConfirmPressed={handleConfirmPressed}
			/>
		</div>
	);
};

const ArchiveOrUnarchiveCard = ({
	handleArchiveClick,
	handleRestoreClick,
	isArchiveSelected,
	isRestoreSelected,
}) => {
	return (
		<div className="archiveOrUnarchiveCard">
			<div className="archiveOrUnarchiveCardTitle">
				Would you like to{" "}
				<b style={{ color: "#B53943" }}>archive an existing project</b>{" "}
				or{" "}
				<b style={{ color: "#568C5B" }}>restore an archived project?</b>
			</div>
			<div className="archiveOrUnarchiveCardOptions">
				<Button
					sx={{ fontSize: "1.2rem", textTransform: "none" }}
					variant={isArchiveSelected ? "contained" : "outlined"}
					color="archive"
					onClick={() => {
						handleArchiveClick();
					}}
				>
					Archive Project
				</Button>
				<Button
					sx={{ fontSize: "1.2rem", textTransform: "none" }}
					variant={isRestoreSelected ? "contained" : "outlined"}
					color="archiveRestore"
					onClick={() => {
						handleRestoreClick();
					}}
				>
					Restore Project
				</Button>
			</div>
		</div>
	);
};

export default ArchiveManager;
